/* =========================================================
   DIR'S FACTORY STORE
   Collection Product Grid V2
   assets/df-collection-v2.js
   ========================================================= */

(() => {
  'use strict';

  /* =========================================================
     SELECTORS
     ========================================================= */

  const SELECTORS = {
    section: '[data-collection-v2]',

    drawer: '[data-df-filter-drawer]',
    panel: '.df-v2-filter-drawer__panel',
    overlay: '.df-v2-filter-drawer__overlay',

    openButton: '[data-df-filter-open]',
    closeButton: '[data-df-filter-close]',
    clearButton: '[data-df-filter-clear]',

    filterForm: '.df-v2-uniqlo-filter',
    applyButton: '.df-v2-uniqlo-filter button[type="submit"]',
    sortSelect: '.df-v2-sort__select',

    productGrid: '#ProductGridContainer',

    desktopDisclosure: '.facets__disclosure',
    desktopSummary: '.facets__summary',
    desktopSummaryLabel: '.facets__summary-label',

    filterInput:
      '.facets input[type="checkbox"], .facets input[type="number"]'
  };

  const instances = new WeakMap();

  /* =========================================================
     COLLECTION CONTROLLER
     ========================================================= */

  class DFCollectionV2 {
    constructor(section) {
      this.section = section;

      this.drawer =
        section.querySelector(SELECTORS.drawer);

      this.panel =
        this.drawer?.querySelector(SELECTORS.panel) || null;

      this.overlay =
        this.drawer?.querySelector(SELECTORS.overlay) || null;

      this.openButtons = Array.from(
        section.querySelectorAll(SELECTORS.openButton)
      );

      this.closeButtons = Array.from(
        section.querySelectorAll(SELECTORS.closeButton)
      );

      this.filterForm =
        section.querySelector(SELECTORS.filterForm);

      this.applyButton =
        section.querySelector(SELECTORS.applyButton);

      this.sortSelect =
        section.querySelector(SELECTORS.sortSelect);

      this.productGrid =
        section.querySelector(SELECTORS.productGrid);

      this.lastFocusedElement = null;
      this.abortController = null;
      this.isLoading = false;

      this.handleOpen =
        this.handleOpen.bind(this);

      this.handleClose =
        this.handleClose.bind(this);

      this.handleOverlayClick =
        this.handleOverlayClick.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleSectionClick =
        this.handleSectionClick.bind(this);

      this.handleFilterSubmit =
        this.handleFilterSubmit.bind(this);

      this.handleSortChange =
        this.handleSortChange.bind(this);

      this.handleFilterInputChange =
        this.handleFilterInputChange.bind(this);

      this.init();
    }

    /* =========================================================
       INITIALIZATION
       ========================================================= */

    init() {
      this.openButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleOpen
        );
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleClose
        );
      });

      this.overlay?.addEventListener(
        'click',
        this.handleOverlayClick
      );

      this.section.addEventListener(
        'click',
        this.handleSectionClick
      );

      this.section.addEventListener(
        'change',
        this.handleFilterInputChange
      );

      this.filterForm?.addEventListener(
        'submit',
        this.handleFilterSubmit
      );

      this.sortSelect?.addEventListener(
        'change',
        this.handleSortChange
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );

      this.updateFilterBadges();
    }

    destroy() {
      this.openButtons.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleOpen
        );
      });

      this.closeButtons.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleClose
        );
      });

      this.overlay?.removeEventListener(
        'click',
        this.handleOverlayClick
      );

      this.section.removeEventListener(
        'click',
        this.handleSectionClick
      );

      this.section.removeEventListener(
        'change',
        this.handleFilterInputChange
      );

      this.filterForm?.removeEventListener(
        'submit',
        this.handleFilterSubmit
      );

      this.sortSelect?.removeEventListener(
        'change',
        this.handleSortChange
      );

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );
    }

    /* =========================================================
       DRAWER
       ========================================================= */

    handleOpen(event) {
      event.preventDefault();

      if (!this.drawer || this.isLoading) {
        return;
      }

      this.lastFocusedElement =
        event.currentTarget;

      this.drawer.hidden = false;

      document.body.classList.add(
        'df-v2-filter-open'
      );

      this.openButtons.forEach((button) => {
        button.setAttribute(
          'aria-expanded',
          'true'
        );
      });

      requestAnimationFrame(() => {
        const focusable =
          this.getFocusableElements();

        focusable[0]?.focus();
      });
    }

    handleClose(event) {
      event?.preventDefault();

      if (!this.drawer) {
        return;
      }

      this.drawer.hidden = true;

      document.body.classList.remove(
        'df-v2-filter-open'
      );

      this.openButtons.forEach((button) => {
        button.setAttribute(
          'aria-expanded',
          'false'
        );
      });

      if (
        this.lastFocusedElement &&
        document.contains(this.lastFocusedElement)
      ) {
        this.lastFocusedElement.focus();
      }
    }

    handleOverlayClick(event) {
      if (event.target === this.overlay) {
        this.handleClose(event);
      }
    }

    handleKeydown(event) {
      if (
        !this.drawer ||
        this.drawer.hidden
      ) {
        return;
      }

      if (event.key === 'Escape') {
        this.handleClose(event);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable =
        this.getFocusableElements();

      if (!focusable.length) {
        return;
      }

      const firstElement =
        focusable[0];

      const lastElement =
        focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    getFocusableElements() {
      if (!this.drawer) {
        return [];
      }

      const elements =
        this.drawer.querySelectorAll(
          [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
          ].join(',')
        );

      return Array.from(elements).filter(
        (element) => {
          return (
            !element.hasAttribute('hidden') &&
            element.offsetParent !== null
          );
        }
      );
    }

    /* =========================================================
       CLEAR ALL
       ========================================================= */

    handleSectionClick(event) {
      const clearButton =
        event.target.closest(
          SELECTORS.clearButton
        );

      if (
        !clearButton ||
        !this.section.contains(clearButton)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.clearFilters();
    }

    clearFilters() {
      if (this.isLoading) {
        return;
      }

      const targetUrl = new URL(
        this.filterForm?.action ||
          window.location.pathname,
        window.location.origin
      );

      targetUrl.search = '';

      const currentUrl =
        new URL(window.location.href);

      const currentSort =
        this.sortSelect?.value ||
        currentUrl.searchParams.get('sort_by');

      if (currentSort) {
        targetUrl.searchParams.set(
          'sort_by',
          currentSort
        );
      }

      this.renderUrl(targetUrl, {
        historyMode: 'push',
        closeDrawer: true,
        scrollToGrid: true
      });
    }

    /* =========================================================
       SHOW RESULTS
       ========================================================= */

    handleFilterSubmit(event) {
      event.preventDefault();

      if (
        !this.filterForm ||
        this.isLoading
      ) {
        return;
      }

      const targetUrl =
        this.buildFilterUrl();

      this.renderUrl(targetUrl, {
        historyMode: 'push',
        closeDrawer: true,
        scrollToGrid: true
      });
    }

    buildFilterUrl() {
      const targetUrl = new URL(
        this.filterForm.action ||
          window.location.pathname,
        window.location.origin
      );

      targetUrl.search = '';

      const formData =
        new FormData(this.filterForm);

      for (const [key, rawValue] of formData.entries()) {
        const value =
          String(rawValue).trim();

        if (!value) {
          continue;
        }

        targetUrl.searchParams.append(
          key,
          value
        );
      }

      const currentUrl =
        new URL(window.location.href);

      const sortValue =
        this.sortSelect?.value ||
        currentUrl.searchParams.get('sort_by');

      if (
        sortValue &&
        !targetUrl.searchParams.has('sort_by')
      ) {
        targetUrl.searchParams.set(
          'sort_by',
          sortValue
        );
      }

      targetUrl.searchParams.delete('page');

      return targetUrl;
    }

    /* =========================================================
       SORT
       ========================================================= */

    handleSortChange(event) {
      if (this.isLoading) {
        return;
      }

      const sortValue =
        event.currentTarget.value;

      const targetUrl =
        new URL(window.location.href);

      if (sortValue) {
        targetUrl.searchParams.set(
          'sort_by',
          sortValue
        );
      } else {
        targetUrl.searchParams.delete(
          'sort_by'
        );
      }

      targetUrl.searchParams.delete('page');

      this.renderUrl(targetUrl, {
        historyMode: 'push',
        closeDrawer: false,
        scrollToGrid: true
      });
    }

    /* =========================================================
       AJAX RENDER ENGINE
       ========================================================= */

    async renderUrl(
      url,
      {
        historyMode = 'push',
        closeDrawer = false,
        scrollToGrid = false
      } = {}
    ) {
      const targetUrl =
        url instanceof URL
          ? url
          : new URL(
              url,
              window.location.origin
            );

      this.abortController?.abort();

      this.abortController =
        new AbortController();

      this.setLoading(true);

      try {
        const response = await fetch(
          targetUrl.toString(),
          {
            method: 'GET',

            headers: {
              Accept: 'text/html',
              'X-Requested-With':
                'XMLHttpRequest'
            },

            signal:
              this.abortController.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Collection request failed: ${response.status}`
          );
        }

        const html =
          await response.text();

        const responseDocument =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const nextSection =
          responseDocument.querySelector(
            SELECTORS.section
          );

        if (!nextSection) {
          throw new Error(
            'Collection V2 section was not found.'
          );
        }

        const importedSection =
          document.importNode(
            nextSection,
            true
          );

        /*
         * Kelas awal untuk efek fade-in.
         */
        importedSection.classList.add(
          'df-v2-section-entering'
        );

        if (closeDrawer) {
          document.body.classList.remove(
            'df-v2-filter-open'
          );
        }

        this.destroy();

        const previousSection =
          this.section;

        previousSection.replaceWith(
          importedSection
        );

        instances.delete(previousSection);

        initSection(importedSection);

        if (responseDocument.title) {
          document.title =
            responseDocument.title;
        }

        if (historyMode === 'push') {
          window.history.pushState(
            {
              dfCollectionV2: true
            },
            '',
            targetUrl.toString()
          );
        } else if (historyMode === 'replace') {
          window.history.replaceState(
            {
              dfCollectionV2: true
            },
            '',
            targetUrl.toString()
          );
        }

        /*
         * Jalankan fade-in setelah section baru
         * sudah masuk ke DOM.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            importedSection.classList.remove(
              'df-v2-section-entering'
            );
          });
        });

        if (scrollToGrid) {
          this.scrollToProductGrid(
            importedSection
          );
        }

        importedSection.dispatchEvent(
          new CustomEvent(
            'df:collection:rendered',
            {
              bubbles: true,

              detail: {
                url:
                  targetUrl.toString()
              }
            }
          )
        );
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.error(
          '[DF Collection V2]',
          error
        );

        this.setLoading(false);

        this.showErrorMessage(
          'Unable to update products. The page will reload.'
        );

        window.setTimeout(() => {
          window.location.assign(
            targetUrl.toString()
          );
        }, 900);
      }
    }

    /* =========================================================
       LOADING STATE
       ========================================================= */

    setLoading(isLoading) {
      this.isLoading = isLoading;

      this.section.classList.toggle(
        'is-loading',
        isLoading
      );

      this.section.setAttribute(
        'aria-busy',
        String(isLoading)
      );

      this.filterForm
        ?.querySelectorAll(
          'button, input, select'
        )
        .forEach((element) => {
          element.disabled = isLoading;
        });

      this.openButtons.forEach((button) => {
        button.disabled = isLoading;
      });

      if (this.sortSelect) {
        this.sortSelect.disabled =
          isLoading;
      }

      if (this.applyButton) {
        this.applyButton.setAttribute(
          'aria-busy',
          String(isLoading)
        );

        this.applyButton.classList.toggle(
          'is-loading',
          isLoading
        );
      }
    }

    /* =========================================================
       SCROLL MANAGEMENT
       ========================================================= */

    scrollToProductGrid(section) {
      const productGrid =
        section.querySelector(
          SELECTORS.productGrid
        );

      if (!productGrid) {
        return;
      }

      const prefersReducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      const toolbarOffset = 120;

      const targetTop =
        productGrid.getBoundingClientRect().top +
        window.scrollY -
        toolbarOffset;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior:
          prefersReducedMotion
            ? 'auto'
            : 'smooth'
      });
    }

    /* =========================================================
       ERROR MESSAGE
       ========================================================= */

    showErrorMessage(message) {
      document
        .querySelector('.df-v2-error-toast')
        ?.remove();

      const toast =
        document.createElement('div');

      toast.className =
        'df-v2-error-toast';

      toast.setAttribute(
        'role',
        'alert'
      );

      toast.textContent = message;

      document.body.appendChild(toast);

      requestAnimationFrame(() => {
        toast.classList.add(
          'is-visible'
        );
      });
    }

    /* =========================================================
       PREMIUM FILTER BADGES
       ========================================================= */

    handleFilterInputChange(event) {
      if (
        !event.target.matches(
          SELECTORS.filterInput
        )
      ) {
        return;
      }

      this.updateFilterBadges();
    }

    updateFilterBadges() {
      const disclosures =
        this.section.querySelectorAll(
          SELECTORS.desktopDisclosure
        );

      disclosures.forEach(
        (disclosure) => {
          const summary =
            disclosure.querySelector(
              SELECTORS.desktopSummary
            );

          const label =
            summary?.querySelector(
              SELECTORS.desktopSummaryLabel
            ) || summary;

          if (!label) {
            return;
          }

          const checkedCount =
            disclosure.querySelectorAll(
              'input[type="checkbox"]:checked'
            ).length;

          let badge =
            label.querySelector(
              '.df-premium-filter-count'
            );

          if (
            !badge &&
            checkedCount > 0
          ) {
            badge =
              document.createElement(
                'span'
              );

            badge.className =
              'df-premium-filter-count';

            badge.setAttribute(
              'aria-hidden',
              'true'
            );

            label.appendChild(badge);
          }

          if (!badge) {
            return;
          }

          badge.textContent =
            String(checkedCount);

          badge.classList.toggle(
            'is-visible',
            checkedCount > 0
          );
        }
      );
    }
  }

  /* =========================================================
     INSTANCE MANAGEMENT
     ========================================================= */

  function initSection(section) {
    if (
      !section ||
      instances.has(section)
    ) {
      return;
    }

    instances.set(
      section,
      new DFCollectionV2(section)
    );
  }

  function destroySection(section) {
    const instance =
      instances.get(section);

    if (!instance) {
      return;
    }

    instance.destroy();

    instances.delete(section);
  }

  function initAll(root = document) {
    if (
      root.matches?.(
        SELECTORS.section
      )
    ) {
      initSection(root);
    }

    root
      .querySelectorAll?.(
        SELECTORS.section
      )
      .forEach(initSection);
  }

  function findSectionFromEvent(event) {
    if (
      event.target.matches?.(
        SELECTORS.section
      )
    ) {
      return event.target;
    }

    return (
      event.target.querySelector?.(
        SELECTORS.section
      ) || null
    );
  }

  /* =========================================================
     INITIAL PAGE LOAD
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAll();
      },
      {
        once: true
      }
    );
  } else {
    initAll();
  }

  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section =
        findSectionFromEvent(event);

      if (section) {
        destroySection(section);
      }
    }
  );

  /* =========================================================
     BROWSER BACK / FORWARD
     ========================================================= */

  window.addEventListener(
    'popstate',
    () => {
      const section =
        document.querySelector(
          SELECTORS.section
        );

      const instance =
        section
          ? instances.get(section)
          : null;

      if (!instance) {
        window.location.reload();
        return;
      }

      instance.renderUrl(
        new URL(
          window.location.href
        ),
        {
          historyMode: 'none',
          closeDrawer: true,
          scrollToGrid: false
        }
      );
    }
  );
})();